import { TextField } from '@material-ui/core';
import TipsIcon from 'components/TipsIcon';
import React, { useRef, useState } from 'react';
import { useEffect } from 'react';
import styled from 'styled-components';
import { addRemoveRequest, deleteRemoveRequest, generateRandomId, updateFormChanging } from 'utility/general';
import ErrorMessages from 'components/Global/ErrorMessages';
import mapValidators from 'utility/mapValidators';
import { checkValueLength } from 'utility/checkValidations';
import { useDispatch, useSelector } from 'react-redux';
import { SYSTEM_HANDLE_FORCE_CHECK_VALIDATION } from 'store/key';
import usePrevious from 'hooks/usePrevious';
import { debounce } from 'lodash';
import { checkNull } from 'utility/formValidate';
import { setTextValidation } from 'store/textLength/reducer';

const SLayout = styled.div`
    position: relative;
    width: 100%;
`;

const SAbsolute = styled.div`
    position: absolute;
    left: ${(props) => (props.alignTop ? 15 + props.placeholderWidth * 0.75 : 15 + props.placeholderWidth)}px;
    top: ${(props) => (props.alignTop ? '-3px' : '13px')};
    transform: translateY(-25%);
    width: ${(props) => (props.alignTop ? '18px' : '10px')};
    height: 10px;
    background: ${(props) => (props.alignTop ? '#fff' : 'transparent')};
    transition: 0.24s all;
`;

const CustomTextField = ({
    onPressEnter = () => null,
    onChange = () => null,
    debounceChange,
    allowAction = true,
    disableOnPermission = false,
    updateApplicationFormOnDisableChange = -1, // application id
    updateSiteFormOnDisableChange = -1, // site-info id
    onAppFieldValidate,
    value = '',
    isTextArea,
    tooltip = '',
    isNoSpace,
    isTrim,
    isValidValue = () => null,
    forceErrorDisplay = false,
    minNumber = 0,
    validators,
    disabled,
    minLength,
    maxLength,
    numberField,
    changeOnBlur = () => null,
    style = {},
    test = false,
    textAreaMinRow = 5,
    changeOnFocus = () => null,
    canPressEnter = true,
    showErrorWhenOnBlur,
    maxNumber,
    ...rest
}) => {
    const { ...other } = rest;
    const extraProps = {};

    const forceCheckValidation = useSelector((state) => state.system.forceCheckValidation);
    const dispatch = useDispatch();
    const [tempValue, setTempValue] = useState(value);
    const [isHover, setHover] = useState(false);
    const [uid] = useState(generateRandomId());
    const [placeholderWidth, setPlaceHolderWidth] = useState(0);
    const [errorMessage, setErrorMessage] = useState([]);
    const [isOnBlurTriggered, setOnBlurTrigger] = useState(false);
    const previousDisable = usePrevious(`${allowAction}`);

    const [isTextOverflow, setTextOverflow] = useState(false);

    const preValidators = usePrevious(validators);

    useEffect(() => {
        // clear form changing field when component unmount
        return () => {
            updateFormChanging(false);
        };
    }, []);

    useEffect(() => {
        if (validators?.toString() !== preValidators?.toString()) {
            onValidation(value);
        }
        // eslint-disable-next-line
    }, [validators]);

    // check errors first when input render
    useEffect(() => {
        if (!debounceChange) {
            onValidation(value);
            checkAppFieldValidate(value);
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value]);

    useEffect(() => {
        if (debounceChange) {
            setTempValue(value);
            onValidation(value);
            checkAppFieldValidate(value);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value]);

    // update to application when disabled property change
    useEffect(() => {
        // updateApplicationFormOnDisableChange = application id
        // previousDisable === undefined means the component is not initialized
        if (previousDisable && (updateApplicationFormOnDisableChange >= 0 || updateSiteFormOnDisableChange >= 0)) {
            if (!rest.name) {
                throw new Error('name is required');
            }

            const isDisabled = getDisableExceptionPermission();

            if (isDisabled) {
                addRemoveRequest({
                    applicationFormId: updateApplicationFormOnDisableChange,
                    name: rest.name,
                    siteInfoId: updateSiteFormOnDisableChange,
                });
            } else {
                deleteRemoveRequest({
                    applicationFormId: updateApplicationFormOnDisableChange,
                    keys: [rest.name],
                    siteInfoId: updateSiteFormOnDisableChange,
                });
            }
        }

        checkAppFieldValidate(debounceChange ? tempValue : value);

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [allowAction, disabled]);

    useEffect(() => {
        if (debounceChange) {
            checkAppFieldValidate(tempValue);
        } else {
            checkAppFieldValidate(value);
        }

        if (errorMessage.length >= 1) {
            let isTextOverflow = false;

            errorMessage.forEach((item) => {
                if (item.includes('This field cannot be longer than')) {
                    isTextOverflow = true;
                }
            });

            if (isTextOverflow) {
                setTextOverflow(true);
                dispatch(
                    setTextValidation({
                        [rest.name ?? generateRandomId()]: true,
                    }),
                );
            }
        } else {
            if (isTextOverflow) {
                dispatch(
                    setTextValidation({
                        [rest.name ?? generateRandomId()]: false,
                    }),
                );

                setTextOverflow(false);
            }
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [errorMessage]);

    const checkAppFieldValidate = (value) => {
        if (onAppFieldValidate !== undefined) {
            const isDisabled = getDisableExceptionPermission();

            if (isDisabled) {
                onAppFieldValidate(rest.name, true);
            } else {
                if (errorMessage?.length > 0) {
                    return onAppFieldValidate(rest.name, false);
                }

                if (numberField) {
                    let isNumberValidationPass = true;

                    if (checkNull(value)) {
                        isNumberValidationPass = false;
                    }

                    if (numberField && value < minNumber) {
                        isNumberValidationPass = false;
                    }

                    if (numberField && value > maxNumber) {
                        isNumberValidationPass = false;
                    }

                    return onAppFieldValidate(rest.name, isNumberValidationPass);
                }

                if (value) {
                    onAppFieldValidate(rest.name, true);
                } else {
                    onAppFieldValidate(rest.name, false);
                }
            }
        }
    };

    // For 'forceCheckValidation' case
    useEffect(() => {
        if (forceCheckValidation) {
            if (debounceChange) {
                onValidation(tempValue);
            } else {
                onValidation(value);
            }

            dispatch({
                type: SYSTEM_HANDLE_FORCE_CHECK_VALIDATION,
                payload: {
                    forceCheckValidation: false,
                },
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [forceCheckValidation]);

    // useEffect(() => {
    //     onValidation(value);

    //     // eslint-disable-next-line react-hooks/exhaustive-deps
    // }, [validators]);

    // logic to force error display
    useEffect(() => {
        if (forceErrorDisplay || onAppFieldValidate) {
            if (debounceChange) {
                setTimeout(() => {
                    setOnBlurTrigger(true);
                }, 300);
            } else {
                setOnBlurTrigger(true);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [forceErrorDisplay, onAppFieldValidate]);

    if (isTextArea) {
        extraProps.multiline = true;
        extraProps.rows = textAreaMinRow;
    }

    const updateParent = (e) => {
        onChange(e);
        updateFormChanging(false);
        if (!showErrorWhenOnBlur) {
            if (debounceChange) {
                setTimeout(() => {
                    setOnBlurTrigger(true);
                }, 300);
            } else {
                setOnBlurTrigger(true);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    };

    const onValueChange = (e) => {
        let value = e.target.value;
        if (numberField) {
            value = parseInt(value);

            if (isNaN(value)) {
                value = 0;
            }

            if (value > 1999999999) {
                value = 1999999999;
            }
        }

        if (isNoSpace) {
            value = value.replace(/\s/g, '');
        }
        e.target.value = value;

        const { name, value: newValue } = e.target;

        if (debounceChange) {
            updateFormChanging(true);
            setTempValue(newValue);
            reference.current({
                target: {
                    name,
                    value: newValue,
                },
            });
        } else {
            updateParent(e);
        }
    };

    const reference = useRef(debounce(updateParent, 300));

    const handleOnBlur = (e) => {
        if (debounceChange) {
            setTimeout(() => {
                setOnBlurTrigger(true);
            }, 300);
        } else {
            setOnBlurTrigger(true);
        }

        setHover(false);
        let originalValue = e.target.value;
        let updatedValue = e.target.value;
        if (isTrim) {
            updatedValue = updatedValue.trim();
        }

        if (originalValue !== updatedValue) {
            e.target.value = updatedValue;
            onChange(e);
        }
    };

    const onValidation = (valueToCheck) => {
        let tempError = mapValidators(valueToCheck, validators);
        tempError = [...tempError, ...checkValueLength(valueToCheck, minLength, maxLength)];

        let isUnderMaxLengthLimit = true;

        tempError.forEach((item) => {
            if (item.startsWith('This field cannot be longer')) {
                isUnderMaxLengthLimit = false;
            }
        });

        isValidValue(tempError.length === 0, isUnderMaxLengthLimit);
        setErrorMessage(tempError);
    };

    const alignTop = () => {
        return !!value || isHover;
    };

    const getDisableExceptionPermission = () => {
        if (disabled) {
            return true;
        }

        if (allowAction === false) {
            return true;
        }

        return false;
    };

    const getDisableStatus = () => {
        if (disableOnPermission) {
            return true;
        }

        if (disabled) {
            return true;
        }

        if (allowAction === false) {
            return true;
        }

        return false;
    };

    useEffect(() => {
        // get placeholder width for tooltip positioning, only check when tooltip exist
        if (tooltip) {
            const height = document.querySelector(`#${uid} label`)?.offsetWidth;

            if (height !== undefined) {
                setPlaceHolderWidth(height);
            }
        }
    }, [tooltip, uid]);

    const extraInputProps = {};
    const extraStyle = {};
    const extraInputLabelProps = {};
    if (numberField) {
        extraInputProps.type = 'number';
        extraInputProps.pattern = '[0-9]{10}';
    }

    if (numberField) {
        extraStyle.width = 250;
    }

    if (getDisableStatus()) {
        extraInputLabelProps.shrink = true;
    }

    return (
        <SLayout id={uid} style={{ ...extraStyle, ...style }}>
            <TextField
                InputProps={{
                    style: {
                        fontSize: 14,
                        background: getDisableStatus() ? '#F7F7F7' : 'initial',
                    },
                    ...extraInputProps,
                }}
                onKeyPress={(ev) => {
                    if (numberField) {
                        const currentValue = debounceChange ? tempValue ?? '' : value;

                        if (!currentValue && currentValue !== 0) {
                            const inputValue = parseInt(ev.key);

                            if (isNaN(inputValue)) {
                                return ev.preventDefault();
                            }
                        }
                    }

                    if (ev.key === 'Enter') {
                        if (!canPressEnter) {
                            ev.preventDefault();
                        }
                        onPressEnter();
                    }
                }}
                InputLabelProps={{
                    style: {
                        fontSize: 14,
                    },
                    ...extraInputLabelProps,
                }}
                onBlur={(e) => {
                    handleOnBlur(e);
                    changeOnBlur({
                        target: {
                            name: rest.name,
                            value: e.target.value,
                        },
                    });

                    if (tooltip) {
                        setHover(false);
                    }
                }}
                onFocus={(e) => {
                    if (tooltip) {
                        setHover(true);
                    }
                    changeOnFocus(e);
                }}
                onChange={onValueChange}
                onPaste={(e) => {
                    // prevent paste e character to the field
                    const value = e.clipboardData.getData('text');
                    if (numberField && value.includes('e')) {
                        return e.preventDefault();
                    }

                    if (numberField) {
                        const currentValue = debounceChange ? tempValue ?? '' : value;

                        if (!currentValue && currentValue !== 0) {
                            const inputValue = parseInt(e.key);

                            if (isNaN(inputValue)) {
                                e.preventDefault();
                            }
                        }
                    }
                }}
                variant="outlined"
                multiline={isTextArea}
                fullWidth={true}
                size={'small'}
                value={debounceChange ? tempValue ?? '' : value || ''}
                disabled={getDisableStatus()}
                error={errorMessage.length > 0 && isOnBlurTriggered}
                onKeyDown={(e) => {
                    // prevent entering 'e' character when number input field
                    if (numberField && e.keyCode === 69) {
                        e.preventDefault();
                    }
                }}
                {...rest}
                {...extraProps}
                {...other}
            />
            {tooltip && (
                <SAbsolute alignTop={alignTop()} placeholderWidth={placeholderWidth}>
                    <TipsIcon title={tooltip} fontSize={alignTop() ? 10 : 11} />
                </SAbsolute>
            )}
            <ErrorMessages isOnBlurTriggered={isOnBlurTriggered} errorMessage={errorMessage} />
            {numberField && !checkNull(value) && value < minNumber && (
                <div style={{ paddingLeft: 10, fontSize: 12, color: 'red', marginTop: 8 }}>
                    Number Cannot smaller than {minNumber}
                </div>
            )}
            {numberField && !checkNull(value) && value > maxNumber && (
                <div style={{ paddingLeft: 10, fontSize: 12, color: 'red', marginTop: 8 }}>
                    Number Cannot larger than {maxNumber}
                </div>
            )}
        </SLayout>
    );
};

export default CustomTextField;
